"use client";

import {
  ChangeEvent,
  DragEvent,
  forwardRef,
  memo,
  MouseEventHandler,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Grip,
  MousePointerClickIcon,
  Plus,
  Ribbon,
  Settings2Icon,
  Trash2,
} from "lucide-react";
import { DragDropContext, Draggable, Droppable, OnDragEndResponder } from "@hello-pangea/dnd";
import { catchError, cn } from "@erp/shared-libs/utils";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { LoadingImage } from "./loading-image";
import { useMutation } from "@tanstack/react-query";
import { useControllableState } from "@erp/shared-libs/hooks";
import { MediaFile } from "@erp/shared-libs/common";
import { Language } from "@erp/shared-libs/variables";
import {
  DEFAULT_IMAGE_MAX_DIMENSION,
  DEFAULT_IMAGE_MAX_SIZE,
  mediaUploadRule,
  uploadMediasPiplineService,
} from "@erp/frontend-api/services";
import { MediaAltDialog, MediaAltDialogValue } from "../forms/media-alt-dialog";

const DEFAULT_IMAGE_DOCK_HEIGHT = 56;
const DEFAULT_IMAGE_LIST_LIMIT = 12;

export type ImageUploaderRef = {
  clear: () => void;
  getImageFiles: () => MediaFile[];
};

interface ImageUploaderProps {
  name?: string;
  imageFiles: MediaFile[];
  className?: string;
  imageDockHeight?: number;
  imageListLimit?: number;
  imageMaxSize?: number;
  imageMaxDimension?: number;
  multiple?: boolean;
  exhibitionMediaId?: string;
  onExhibitionMediaIdChange?: (mediaId: string) => void;
  onMediaFilesChange: (files: MediaFile[]) => void;
  onClick?: () => void;
  disabled?: boolean;
  selectionMode?: boolean;
  disabelUpload?: boolean;
  /** 唯讀模式：禁止上傳、刪除、拖曳排序、Alt 設定等所有寫入操作，不改變樣式，僅保留切換展示圖功能 */
  readonly?: boolean;
  /**
   * 租戶的內容語言（含預設語言），給替代文字對話框用；呼叫端沒接內容語言時不傳，
   * 對話框會退回單語言模式（不渲染語言列，畫面與舊版相同）
   */
  languages?: { id: number; label: string }[];
  /**
   * 租戶的內容預設語言 id；不傳時退回 `Language.None`，只在沒帶 `languages` 的單語言模式下有意義
   */
  defaultLanguageId?: number;
}

export const ImageUploader = memo(
  forwardRef<ImageUploaderRef, ImageUploaderProps>(
    (
      {
        name,
        className,
        onMediaFilesChange: onImageFilesChange,
        imageFiles,
        multiple,
        exhibitionMediaId: exhibitionMediaIdProp,
        onExhibitionMediaIdChange,
        imageDockHeight: imageDockHeightProp,
        imageListLimit: imageListLimitProp,
        imageMaxSize: imageMaxSizeProp,
        imageMaxDimension: imageMaxDimensionProp,
        onClick,
        disabled,
        selectionMode,
        disabelUpload,
        readonly,
        languages,
        defaultLanguageId,
        ...props
      },
      ref
    ) => {
      // =========================================================
      // Hooks
      // =========================================================
      const { t } = useTranslation(["common"]);
      const fileInputRef = useRef<HTMLInputElement>(null);
      const imageListRef = useRef<HTMLDivElement>(null);
      const [isAltDialogOpen, setIsAltDialogOpen] = useState(false);
      const [editingAltImage, setEditingAltImage] = useState<MediaFile | undefined>(undefined);
      const [exhibitionMediaId, setExhibitionMediaId] = useControllableState<string>({
        defaultProp: selectionMode ? (exhibitionMediaIdProp ?? "") : (imageFiles[0]?.mediaId ?? ""),
        onChange: (mediaId) => {
          onExhibitionMediaIdChange?.(mediaId);
        },
      });

      // =========================================================
      // Mutation
      // =========================================================
      const { mutateAsync: uploadMediaAsync } = useMutation({
        mutationKey: [...uploadMediasPiplineService.key],
        mutationFn: uploadMediasPiplineService.fn,
      });

      // =========================================================
      // Variables
      // =========================================================
      const imageDockHeight = imageDockHeightProp ?? DEFAULT_IMAGE_DOCK_HEIGHT;
      const imageListLimit = imageListLimitProp ?? DEFAULT_IMAGE_LIST_LIMIT;
      const imageMaxSize = imageMaxSizeProp ?? DEFAULT_IMAGE_MAX_SIZE;
      const imageMaxDimension = imageMaxDimensionProp ?? DEFAULT_IMAGE_MAX_DIMENSION;

      /**
       * 替代文字對話框的內容預設語言：呼叫端沒接內容語言時退回 `Language.None`，
       * 只在下面的單語言模式（`resolvedLanguages` 只有一筆）下有意義
       */
      const resolvedDefaultLanguageId = defaultLanguageId ?? Language.None;

      /**
       * 替代文字對話框的語言清單：呼叫端沒帶 `languages` 就合成一筆單語言，
       * 對話框只有一種語言時不渲染語言列，畫面與舊版相同
       */
      const resolvedLanguages = languages ?? [{ id: resolvedDefaultLanguageId, label: "" }];

      // =========================================================
      // Functions
      // =========================================================
      /**
       * 上移展示的圖片
       */
      const handleMoveUpExhibitionImage = useCallback(
        (e?: React.MouseEvent<HTMLButtonElement>) => {
          e?.stopPropagation();
          const currentIndex = imageFiles.findIndex(({ mediaId: id }) => id === exhibitionMediaId);
          const prevIndex = Math.max(currentIndex - 1, 0);

          setExhibitionMediaId(() => {
            const index = prevIndex;
            return imageFiles[index].mediaId;
          });
        },
        [exhibitionMediaId, imageFiles, setExhibitionMediaId]
      );

      /**
       * 下移展示的圖片
       */
      const handleMoveDownExhibitionImage = useCallback(
        (e?: React.MouseEvent<HTMLButtonElement>) => {
          e?.stopPropagation();
          const currentIndex = imageFiles.findIndex(({ mediaId: id }) => id === exhibitionMediaId);
          const nextIndex = Math.min(currentIndex + 1, imageFiles.length - 1);

          setExhibitionMediaId(() => {
            return imageFiles[nextIndex].mediaId;
          });
        },
        [exhibitionMediaId, imageFiles, setExhibitionMediaId]
      );

      /**
       * 使用鍵盤上下鍵切換展示的圖片
       */
      const handleKeyTriggerMoveExhibitionImage = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === "ArrowUp") {
            handleMoveUpExhibitionImage();
          } else if (e.key === "ArrowDown") {
            handleMoveDownExhibitionImage();
          }
        },
        [handleMoveUpExhibitionImage, handleMoveDownExhibitionImage]
      );

      /**
       * 選擇展示的圖片
       */
      const handleSelectMedia = useCallback(
        (mediaId?: string) => (e: React.MouseEvent<HTMLDivElement>) => {
          e.stopPropagation();
          if ((selectionMode && exhibitionMediaId === mediaId) || !mediaId) {
            setExhibitionMediaId("");
            return;
          }
          setExhibitionMediaId(mediaId);
        },
        [exhibitionMediaId, selectionMode, setExhibitionMediaId]
      );

      /**
       * 移除圖片
       */
      const handleRemoveImage = useCallback(
        (id: string) => () => {
          const targetIndex = imageFiles.findIndex((file) => file.mediaId === id);
          const result = imageFiles.filter((file) => file.mediaId !== id);

          onImageFilesChange(result);

          if (exhibitionMediaId === id) {
            const nextIndex = Math.max(targetIndex - 1, 0);

            setExhibitionMediaId(result[nextIndex]?.mediaId ?? "");
          }
        },
        [exhibitionMediaId, imageFiles, onImageFilesChange, setExhibitionMediaId]
      );

      /**
       * 檢查圖片是否合法
       */
      const handleCheckImageValid = useCallback(
        async (files: FileList) => {
          const newFiles: { file: File }[] = [];

          if (files.length + imageFiles.length > imageListLimit) {
            toast.error(`最多上傳 ${imageListLimit} 張圖片`);
            return;
          }

          const validator = mediaUploadRule()
            .allowTypes(["image/jpeg", "image/jpg"], "僅支援 JPG 圖片")
            .maxDimension(imageMaxDimension)
            .maxSize(imageMaxSize)
            .jpegOnly()
            .notCmyk();

          for (const file of files ?? []) {
            const result = await validator.validate(file);

            if (!result.success) {
              toast.error(result.message);
              return;
            }

            newFiles.push({ file });
          }

          const [, results] = await catchError(() => uploadMediaAsync(newFiles));

          return results;
        },
        [imageFiles.length, imageListLimit, imageMaxDimension, imageMaxSize, uploadMediaAsync]
      );

      /**
       * 拖曳調整順序結束
       */
      const handleDragEnd: OnDragEndResponder<string> = useCallback(
        ({ source, destination }) => {
          if (!destination) {
            return;
          }
          const newImageFiles = [...imageFiles];
          const [removed] = newImageFiles.splice(source.index, 1);
          newImageFiles.splice(destination.index, 0, removed);

          onImageFilesChange(newImageFiles);
        },
        [onImageFilesChange, imageFiles]
      );

      /**
       * 處理檔案變更
       */
      const handleFileChange = useCallback(
        async (e: ChangeEvent<HTMLInputElement>) => {
          if (!e.target.files) {
            return;
          }

          const newFiles = await handleCheckImageValid(e.target.files);

          if (!newFiles) {
            // 要清空 input 的值，否則下一個檔案選擇會無法觸發 onChange
            fileInputRef.current!.value = "";
            return;
          }

          const result = [...imageFiles, ...newFiles];

          onImageFilesChange(result);

          if (newFiles.length) {
            setExhibitionMediaId(newFiles[newFiles.length - 1].mediaId ?? "");
          }

          // 要清空 input 的值，否則下一個檔案選擇會無法觸發 onChange
          fileInputRef.current!.value = "";
        },
        [handleCheckImageValid, imageFiles, onImageFilesChange, setExhibitionMediaId]
      );

      /**
       * 處理拖曳進入時的動作
       */
      const handleDragOver = useCallback((e: DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        e.currentTarget.dataset.dragging = "true";
      }, []);

      /**
       * 處理拖曳離開時的動作
       */
      const handleDragLeave = useCallback((e: DragEvent<HTMLLabelElement>) => {
        e.currentTarget.dataset.dragging = "false";
      }, []);

      /**
       * 處理拖曳檔案上傳，檢查並上傳圖片
       */
      const handleDrop = useCallback(
        async (e: DragEvent<HTMLLabelElement>) => {
          e.preventDefault();
          e.currentTarget.dataset.dragging = "false";

          const newFiles = await handleCheckImageValid(e.dataTransfer.files);

          if (!newFiles) {
            // 要清空 input 的值，否則下一個檔案選擇會無法觸發 onChange
            fileInputRef.current!.value = "";
            return;
          }

          const result = [...imageFiles, ...newFiles];

          onImageFilesChange(result);

          if (newFiles.length) {
            setExhibitionMediaId(newFiles[newFiles.length - 1].mediaId ?? "");
          }

          // 要清空 input 的值，否則下一個檔案選擇會無法觸發 onChange
          fileInputRef.current!.value = "";
        },
        [handleCheckImageValid, imageFiles, onImageFilesChange, setExhibitionMediaId]
      );

      /**
       * 進入提示時的動作，延遲 300ms 後隱藏提示
       */
      const handleMouseEnterIntoHint: MouseEventHandler<HTMLDivElement> = useCallback((e) => {
        const target = e.currentTarget;
        const timerId = setTimeout(() => {
          target.dataset.hidden = "true";
        }, 300);
        target.dataset.hiddenTimer = timerId as unknown as string;
      }, []);

      /**
       * 離開提示時的動作，清除隱藏提示的計時器
       */
      const handleMouseLeaveFromHint: MouseEventHandler<HTMLDivElement> = useCallback((e) => {
        clearTimeout(e.currentTarget.dataset.hiddenTimer);
      }, []);

      /**
       * 捲動至展示的圖片
       */
      const scrollToExhibitionImage = useCallback(() => {
        const index = imageFiles.findIndex(({ mediaId: id }) => id === exhibitionMediaId);
        if (index === -1) {
          return;
        }
        imageListRef.current?.scrollTo({
          top: imageDockHeight * index,
          behavior: "smooth",
        });
      }, [imageFiles, imageDockHeight, exhibitionMediaId]);

      /**
       * 確定替代文字：對話框自己會關閉（見 `MediaAltDialog`），這裡只要把值寫回 imageFiles
       */
      const handleConfirmAlt = useCallback(
        (value: MediaAltDialogValue) => {
          const index = imageFiles.findIndex(({ mediaId: id }) => id === editingAltImage?.mediaId);
          if (index === -1) return;

          const newFiles = [...imageFiles];
          newFiles[index] = {
            ...newFiles[index],
            alt: value.alt,
            altTranslations: value.altTranslations,
          };

          onImageFilesChange(newFiles);
        },
        [editingAltImage?.mediaId, imageFiles, onImageFilesChange]
      );

      /**
       * 處理點擊遮罩的事件，避免觸發其他事件
       */
      const handleClickMask = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        // 如果點擊的是遮罩，則不觸發任何事件
        e.stopPropagation();
        e.preventDefault();
      }, []);

      const handleSetAlt = useCallback(() => {
        const file = imageFiles.find((file) => file.mediaId === exhibitionMediaId);
        if (!file) {
          return;
        }
        setEditingAltImage(file);
        setIsAltDialogOpen(true);
      }, [exhibitionMediaId, imageFiles]);

      // =========================================================
      // Effects
      // =========================================================
      useImperativeHandle(ref, () => ({
        clear: () => {
          onImageFilesChange([]);
          setExhibitionMediaId("");
          onImageFilesChange?.([]);
        },
        getImageFiles: () => [...imageFiles],
      }));

      useEffect(() => {
        scrollToExhibitionImage();
      }, [exhibitionMediaId, scrollToExhibitionImage]);

      // 如果是 async value 或是初始值沒有設定，則使用第一張圖片的 mediaId
      useEffect(() => {
        if (selectionMode) {
          return;
        }

        setExhibitionMediaId((prev) => {
          if (prev && !imageFiles.some((file) => file.mediaId === prev)) {
            return imageFiles[0]?.mediaId || "";
          }

          return prev || imageFiles[0]?.mediaId || "";
        });
      }, [selectionMode, imageFiles, setExhibitionMediaId]);

      // =========================================================
      // Render
      // =========================================================
      return (
        <>
          {/* 替代文字設定 */}
          <MediaAltDialog
            open={isAltDialogOpen}
            onOpenChange={setIsAltDialogOpen}
            imageUrl={editingAltImage?.url}
            value={{
              alt: editingAltImage?.alt ?? "",
              altTranslations: editingAltImage?.altTranslations ?? {},
            }}
            languages={resolvedLanguages}
            defaultLanguageId={resolvedDefaultLanguageId}
            onConfirm={handleConfirmAlt}
          />

          {/* 圖片上傳 */}
          <div
            tabIndex={0}
            className={cn(
              "border-input relative flex rounded-xl border p-2",
              "focus-within:ring-ring focus-within:outline-hidden focus-within:ring-2 focus-within:ring-offset-2",
              className
            )}
            onClick={onClick}
            onKeyDown={handleKeyTriggerMoveExhibitionImage}
          >
            {/* Disabled 遮罩 */}
            {disabled ? (
              <div
                onClick={handleClickMask}
                className="bg-primary-50/50 absolute left-0 top-0 z-10 h-full w-full cursor-not-allowed rounded-xl"
              />
            ) : null}

            {/* 隱藏的按鈕，讓使用者可以使用 Tab 鍵選取 */}
            <button type="button" className="absolute left-0 top-0 size-0" name={name} />

            {/* 圖片欄 */}

            {readonly && imageFiles.length === 0 ? null : (
              <div className="flex shrink-0 flex-col items-center gap-2 pr-2">
                {/* 上按鈕 */}
                <Button
                  type="button"
                  className="shrink-0 px-7"
                  size="xs"
                  variant="dark"
                  disabled={disabled}
                  onClick={handleMoveUpExhibitionImage}
                >
                  <ChevronUp />
                </Button>
                {/* 圖片 */}
                <div
                  ref={imageListRef}
                  className="relative flex flex-auto flex-col overflow-y-auto"
                >
                  {/* 圖片列表 */}
                  <div className="px-2 py-1">
                    <DragDropContext onDragEnd={handleDragEnd}>
                      <Droppable droppableId="imageList">
                        {(droppableProvider) => (
                          <>
                            <div
                              className="grid grid-cols-1 place-content-center gap-2.5"
                              ref={droppableProvider.innerRef}
                              {...droppableProvider.droppableProps}
                            >
                              {imageFiles.map(({ mediaId, url: image }, index) => {
                                return (
                                  <Draggable
                                    key={mediaId}
                                    draggableId={mediaId}
                                    index={index}
                                    isDragDisabled={disabled || selectionMode || readonly}
                                  >
                                    {(provided) => {
                                      const child = (
                                        <div
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          {...provided.dragHandleProps}
                                          style={{
                                            ...provided.draggableProps.style,
                                          }}
                                        >
                                          {/* 圖片預覽，限制最大寬高 */}
                                          <div
                                            className="relative"
                                            style={{
                                              maxWidth: imageDockHeight,
                                              maxHeight: imageDockHeight,
                                            }}
                                            onClick={handleSelectMedia(mediaId)}
                                          >
                                            {/* 多給一點解析度，縮放時不會失真 */}
                                            <LoadingImage
                                              src={image}
                                              alt="preview"
                                              width={imageDockHeight}
                                              height={imageDockHeight}
                                              data-highlight={exhibitionMediaId === mediaId}
                                              containerClassName={cn(
                                                "ring-offset-on-background aspect-square ring-offset-2",
                                                exhibitionMediaId === mediaId &&
                                                  "ring-primary! ring-2",
                                                selectionMode &&
                                                  exhibitionMediaId !== mediaId &&
                                                  "opacity-40"
                                              )}
                                            />
                                            {/* 如果是第一張圖片而且 ”開啟排序“，顯示 Ribbon，代表是主要圖片*/}
                                            {imageFiles[0].mediaId === mediaId && !selectionMode ? (
                                              <div
                                                className="bg-primary text-on-background z-9 absolute bottom-0 right-0 flex items-center justify-center
                                                  rounded-sm bg-opacity-35 p-0.5"
                                              >
                                                <Ribbon className="size-3" />
                                              </div>
                                            ) : null}
                                            {/* 如果是 "關閉排序" 且有選擇到這個圖片 */}
                                            {mediaId === exhibitionMediaId && selectionMode ? (
                                              <div
                                                className="bg-primary text-on-background z-9 absolute bottom-0 right-0 flex items-center justify-center
                                                  rounded-br-lg rounded-tl-sm bg-opacity-35 p-0.5"
                                              >
                                                <Check className="size-3" />
                                              </div>
                                            ) : null}
                                          </div>
                                        </div>
                                      );
                                      // 停用：在 dialog 中拖曳時 portal 會導致位置錯亂，且會造成手機版問題
                                      // return snapshot.isDragging
                                      //   ? ReactDOM.createPortal(child, document.body)
                                      //   : child;
                                      return child;
                                    }}
                                  </Draggable>
                                );
                              })}

                              {droppableProvider.placeholder}

                              {/* 加號按鈕 */}
                              {imageFiles.length < imageListLimit && !disabelUpload && !readonly ? (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  className="border-input text-input flex aspect-square h-auto w-full items-center justify-center rounded-sm
                                    border border-dashed"
                                  disabled={disabled}
                                  onClick={() => {
                                    fileInputRef.current?.click();
                                  }}
                                >
                                  <Plus />
                                </Button>
                              ) : null}
                            </div>
                          </>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </div>

                  {/* 拖曳提示 */}
                  <div
                    data-hidden={imageFiles.length > 0 || selectionMode || readonly}
                    className="border-foreground/50 bg-background/50 absolute left-0 top-0 flex h-full w-full flex-col items-center
                      justify-center rounded-sm border border-dashed p-2 transition-all duration-300
                      data-[hidden=true]:invisible data-[hidden=true]:opacity-0"
                    onMouseEnter={handleMouseEnterIntoHint}
                    onMouseLeave={handleMouseLeaveFromHint}
                  >
                    <Grip className="text-foreground mb-2 size-8" />
                    <p className="text-foreground text-center text-sm">拉動以調整排序</p>
                  </div>
                </div>
                {/* 下按鈕 */}
                <Button
                  type="button"
                  className="shrink-0 rounded-sm px-7"
                  size="xs"
                  variant="dark"
                  disabled={disabled}
                  onClick={handleMoveDownExhibitionImage}
                >
                  <ChevronDown />
                </Button>
              </div>
            )}

            {/* 展示圖片 及 上傳 */}
            <div className="relative flex flex-1 overflow-hidden">
              <Label
                onDragLeave={readonly ? undefined : handleDragLeave}
                onDragOver={readonly ? undefined : handleDragOver}
                onDrop={readonly ? undefined : handleDrop}
                className={cn(
                  `border-input bg-background data-[dragging=true]:border-primary relative flex flex-1 cursor-pointer
                  flex-col items-center justify-center overflow-hidden rounded-lg border border-dashed text-sm
                  data-[dragging=true]:border-2`,
                  (disabled || readonly) && "pointer-events-none"
                )}
              >
                {imageFiles.find((file) => file.mediaId === exhibitionMediaId)?.url ? (
                  <LoadingImage
                    src={imageFiles.find((file) => file.mediaId === exhibitionMediaId)?.url}
                    containerClassName="w-full!"
                    alt="preview"
                    width={650}
                    height={650}
                  />
                ) : (
                  <div className="flex flex-col items-center p-4 text-center">
                    {disabelUpload || readonly ? (
                      <div className="flex items-center space-x-2">
                        <p>
                          {imageFiles.length > 0
                            ? "點擊縮圖預覽圖片"
                            : "目前沒有圖片，請先上傳圖片"}
                        </p>
                        <MousePointerClickIcon size={16} />
                      </div>
                    ) : (
                      <>
                        <Plus className="text-input mb-4" />
                        <p>可點擊選取或拖曳{multiple ? "多張" : "一張"}圖片至框線上傳</p>
                      </>
                    )}
                  </div>
                )}
                {disabelUpload || readonly ? null : (
                  <Input
                    multiple
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg, .jpeg"
                    className="-z-1 absolute right-0 top-0 h-full w-full border opacity-0"
                    onChange={handleFileChange}
                    disabled={disabled}
                    {...props}
                  />
                )}
              </Label>
            </div>

            {/* 設定按鈕，放 label 外面，因為點擊 label 會同時觸發 button */}
            {imageFiles.find((file) => file.mediaId === exhibitionMediaId)?.url &&
            !disabelUpload &&
            !readonly ? (
              <div className="z-2 absolute bottom-4 right-4 flex items-center justify-between space-x-2">
                <Button
                  type="button"
                  variant="outlinePrimary"
                  size="xs"
                  className="px-4"
                  aria-label={t("common:media.alt.dialog.title")}
                  onClick={handleSetAlt}
                >
                  <Settings2Icon />
                </Button>
                <Button
                  type="button"
                  variant="outlinePrimary"
                  size="xs"
                  className="px-4"
                  aria-label={t("common:media.image.remove")}
                  onClick={handleRemoveImage(exhibitionMediaId)}
                >
                  <Trash2 />
                </Button>
              </div>
            ) : null}
          </div>
        </>
      );
    }
  )
);
